/**
 * Customer survey page (2026-10-07) - /survey/:surveyId[?c=<personal link token>].
 * Businesses share this link from the dashboard's Surveys screen (a general link, or one signed
 * personal link per customer so the answer is linked to them). One question per screen, built for
 * phones: NPS as 0-10 buttons, ratings as stars, pick-one lists, yes/no and written answers.
 * Questions come from the public GET /surveys/public/:id; answers go to POST /surveys/:id/submit,
 * which checks them again server-side (backend/src/routes/surveys.routes.ts).
 * Same page as mangoes-unified-template's, dressed in this site's own style: the masthead/eyebrow
 * intro and OptionCard choices used across the Noosa order flow.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SuccessState from '../components/SuccessState';
import OptionCard from '../components/OptionCard';
import { usePortal } from '../context/PortalContext';
import { getApiBaseUrl } from '../services/api';
import './SurveyPage.css';

const STAR_WORDS = ['', 'Poor', 'Not great', 'OK', 'Good', 'Excellent'];

const isAnswered = (v) => v !== undefined && v !== null && String(v).trim() !== '';

export default function SurveyPage() {
  const { surveyId } = useParams();
  const [searchParams] = useSearchParams();
  const linkToken = searchParams.get('c') || '';
  const { branding } = usePortal();

  // status: loading | ready | notFound | closed | error | done | already
  const [status, setStatus] = useState('loading');
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0);
  const [direction, setDirection] = useState(1);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const advanceTimer = useRef(null);

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      const qs = linkToken ? `?c=${encodeURIComponent(linkToken)}` : '';
      const res = await fetch(`${getApiBaseUrl()}/surveys/public/${encodeURIComponent(surveyId)}${qs}`);
      const data = await res.json().catch(() => ({}));
      if (res.status === 404) return setStatus('notFound');
      if (res.status === 410 || data.closed) {
        setSurvey(data.data || null);
        return setStatus('closed');
      }
      if (!res.ok || !data.data) return setStatus('error');
      setSurvey(data.data);
      setStatus(data.data.alreadyAnswered ? 'already' : 'ready');
    } catch {
      setStatus('error');
    }
  }, [surveyId, linkToken]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (survey?.title) document.title = survey.business?.name ? `${survey.title} - ${survey.business.name}` : survey.title;
  }, [survey]);

  useEffect(() => () => window.clearTimeout(advanceTimer.current), []);

  const questions = survey?.questions || [];
  const total = questions.length;
  const question = questions[step];
  const isLast = step === total - 1;
  const current = question ? answers[question.id] : undefined;
  const canGoOn = question && (!question.required || isAnswered(current));

  const goTo = (next) => {
    window.clearTimeout(advanceTimer.current);
    setError('');
    setDirection(next > step ? 1 : -1);
    setStep(next);
  };

  const submit = async () => {
    const missingIndex = questions.findIndex((q) => q.required && !isAnswered(answers[q.id]));
    if (missingIndex >= 0) {
      goTo(missingIndex);
      setError('Please answer this one before sending.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${getApiBaseUrl()}/surveys/${encodeURIComponent(surveyId)}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers: questions
            .filter((q) => isAnswered(answers[q.id]))
            .map((q) => ({ questionId: q.id, value: typeof answers[q.id] === 'string' ? answers[q.id].trim() : answers[q.id] })),
          ...(linkToken ? { c: linkToken } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) return setStatus('done');
      if (res.status === 409 || data.alreadyAnswered) return setStatus('already');
      if (res.status === 410 || data.closed) return setStatus('closed');
      if (res.status === 429) return setError('Lots of answers coming in from here - give it a minute and tap Send again.');
      const badIndex = data.questionId ? questions.findIndex((q) => q.id === data.questionId) : -1;
      if (badIndex >= 0) goTo(badIndex);
      setError(data.error || "We couldn't send that - please try again.");
    } catch {
      setError("We couldn't reach the server - check your connection and tap Send again.");
    } finally {
      setSubmitting(false);
    }
  };

  /** Single-tap answers move on by themselves, after a beat so the tap registers visually. */
  const answer = (value, autoAdvance) => {
    setAnswers((prev) => ({ ...prev, [question.id]: value }));
    setError('');
    window.clearTimeout(advanceTimer.current);
    if (autoAdvance && !isLast) {
      const from = step;
      advanceTimer.current = window.setTimeout(() => {
        setDirection(1);
        setStep((s) => (s === from ? s + 1 : s));
      }, 320);
    }
  };

  const next = () => {
    if (!canGoOn) {
      setError('Please pick an answer to carry on.');
      return;
    }
    if (isLast) submit();
    else goTo(step + 1);
  };

  const businessName = survey?.business?.name || branding.businessName;

  const renderInput = (q) => {
    const value = answers[q.id];
    switch (q.type) {
      case 'nps':
        return (
          <>
            <div className="svy-nps" role="radiogroup" aria-label={q.text}>
              {Array.from({ length: 11 }, (_, n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={value === n}
                  className={`svy-choice-btn${value === n ? ' is-selected' : ''}`}
                  onClick={() => answer(n, true)}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="svy-scale-ends" aria-hidden="true">
              <span>0 - Not likely</span>
              <span>10 - Very likely</span>
            </div>
          </>
        );
      case 'rating':
        return (
          <>
            <div className="svy-stars" role="radiogroup" aria-label={q.text}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={value === n}
                  aria-label={`${n} star${n === 1 ? '' : 's'} - ${STAR_WORDS[n]}`}
                  className={`svy-star${value >= n ? ' is-on' : ''}`}
                  onClick={() => answer(n, true)}
                >
                  ★
                </button>
              ))}
            </div>
            <div className="svy-star-label" aria-live="polite">{value ? STAR_WORDS[value] : 'Tap a star'}</div>
          </>
        );
      case 'yes_no':
        return (
          <div className="svy-yesno" role="group" aria-label={q.text}>
            {[['yes', 'Yes'], ['no', 'No']].map(([v, label]) => (
              <OptionCard key={v} title={label} selected={value === v} onClick={() => answer(v, true)} />
            ))}
          </div>
        );
      case 'multiple_choice':
        return (
          <div className="svy-list" role="group" aria-label={q.text}>
            {(q.options || []).map((option) => (
              <OptionCard key={option} title={option} selected={value === option} onClick={() => answer(option, true)} />
            ))}
          </div>
        );
      default:
        return (
          <textarea
            className="svy-text"
            value={value || ''}
            onChange={(e) => answer(e.target.value.slice(0, 5000), false)}
            placeholder="Type your answer"
            aria-label={q.text}
            maxLength={5000}
          />
        );
    }
  };

  const stateCard = (icon, title, message, action) => (
    <div className="svy-card svy-state">
      <div className="svy-state-icon" aria-hidden="true">{icon}</div>
      <h2>{title}</h2>
      <p>{message}</p>
      {action}
    </div>
  );

  let body;
  if (status === 'loading') {
    body = (
      <div className="svy-card" aria-busy="true" aria-label="Loading the survey">
        <div className="svy-skeleton" style={{ width: '60%' }} />
        <div className="svy-skeleton" style={{ width: '85%' }} />
        <div className="svy-skeleton" style={{ width: '40%', height: 48 }} />
      </div>
    );
  } else if (status === 'notFound') {
    body = stateCard('🔍', "We couldn't find this survey", 'The link may be incomplete - try opening it again from the message you were sent.', <Link to="/" className="btn btn-primary">Visit our site</Link>);
  } else if (status === 'closed') {
    body = stateCard('🗓️', 'This survey has closed', `Thanks for wanting to help${businessName ? ` ${businessName}` : ''} - it's no longer taking answers.`, <Link to="/" className="btn btn-primary">Visit our site</Link>);
  } else if (status === 'error') {
    body = stateCard('📡', "The survey didn't load", 'Check your connection and try again.', <button type="button" className="btn btn-primary" onClick={load}>Try again</button>);
  } else if (status === 'already') {
    body = stateCard('💛', "You've already answered", `Thanks - we've got your answers${businessName ? `, and ${businessName} really appreciates it` : ''}.`, <Link to="/" className="btn btn-secondary">Visit our site</Link>);
  } else if (status === 'done') {
    body = (
      <div className="svy-card">
        <SuccessState title="Thank you!" message={`Your answers are in${businessName ? ` - ${businessName} reads every one` : ''}.`}>
          <Link to="/" className="btn btn-secondary">Back to the site</Link>
        </SuccessState>
      </div>
    );
  } else if (total === 0) {
    body = stateCard('📝', 'Nothing to answer yet', 'This survey has no questions at the moment.', <Link to="/" className="btn btn-primary">Visit our site</Link>);
  } else {
    body = (
      <>
        <div className="svy-progress">
          <div className="svy-progress-label">
            <span>Question {step + 1} of {total}</span>
            <span>{Math.round((step / total) * 100)}%</span>
          </div>
          <div className="svy-progress-track" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={step + 1} aria-label="Survey progress">
            <div className="svy-progress-fill" style={{ width: `${((step + 1) / total) * 100}%` }} />
          </div>
        </div>
        <div className="svy-card">
          <AnimatePresence mode="wait" initial={false} custom={direction}>
            <motion.div
              key={question.id}
              custom={direction}
              initial={{ opacity: 0, x: 24 * direction }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 * direction }}
              transition={{ duration: 0.18 }}
            >
              <h2 className="svy-question" id="svy-question">{question.text}</h2>
              <div className="svy-optional">{question.required ? ' ' : 'Optional'}</div>
              {renderInput(question)}
            </motion.div>
          </AnimatePresence>
          {error && <div className="svy-error" role="alert">{error}</div>}
          <div className="svy-nav">
            {step > 0 && (
              <button type="button" className="btn btn-secondary" onClick={() => goTo(step - 1)} disabled={submitting}>
                Back
              </button>
            )}
            <button type="button" className="btn btn-primary" onClick={next} disabled={submitting}>
              {isLast ? (submitting ? 'Sending…' : 'Send') : !question.required && !isAnswered(current) ? 'Skip' : 'Next'}
            </button>
          </div>
        </div>
      </>
    );
  }

  const showIntro = survey && (status === 'ready' || status === 'loading');
  const showBusinessLogo = survey?.business?.logoUrl && !branding.logoUrl;

  return (
    <>
      <Header />
      <main className="section" style={{ minHeight: '70vh' }}>
        <div className="svy">
          {showIntro && (
            <div className="masthead svy-masthead">
              {businessName && (
                <span className="eyebrow">
                  {showBusinessLogo && <img src={survey.business.logoUrl} alt="" className="svy-eyebrow-logo" />}
                  {businessName}
                </span>
              )}
              <h1>{survey.title}</h1>
              {survey.description && step === 0 && <p>{survey.description}</p>}
            </div>
          )}
          {body}
        </div>
      </main>
      <Footer />
    </>
  );
}
